import { Card, Colors, Elevation, Icon, Intent, Tag, Tooltip } from '@blueprintjs/core';
import React, { HTMLAttributes } from 'react';
import Box from '@components/Box';
import { TaskDeliverableResource } from 'dy-frontend-http-repository/lib/modules/TaskDeliverable/resources';
import FilePreview from '@app/components/FilePreview';
import { TaskDeliverableType } from 'dy-frontend-http-repository/lib/data/enums';
import Flex from '@app/components/Flex';
import Link from '@app/components/Link';
import { TextFormatUtils } from 'dy-frontend-shared/lib/utils';
import DevText from '@app/components/Text';
import moment from 'moment';
import { fileHashPreview } from '@app/data/consts';
import { FileHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/FileHashPreview/enums';
import Circle from '@app/components/Circle';

export interface TaskDeliverableListGridViewListItemProps {
    taskDeliverable: TaskDeliverableResource;
    actions?: React.ReactNode;
    onOpenViewer: () => void;
}

export type Props = HTMLAttributes<HTMLDivElement> & TaskDeliverableListGridViewListItemProps;

const TaskDeliverableListGridViewListItem: React.FC<Props> = ({ taskDeliverable, actions, onOpenViewer, ...props }) => {
    const renderTaskDeliverableApprovementTag = () => {
        if (taskDeliverable.approved_at) {
            // Approved
            return null;
        }

        // Not approved
        return (
            <>
                <Tag minimal className="mr-1" icon="warning-sign" intent={Intent.WARNING}>
                    Not approved
                </Tag>
                <Circle color={Colors.GRAY2} size="6px" className="mr-1" />
            </>
        );
    };

    const renderTaskDeliverablePreview = () => {
        if (taskDeliverable.file) {
            let previewUrl: string | null = null;
            if (taskDeliverable.file.preview_path) {
                previewUrl = fileHashPreview.getPreviewUrl(taskDeliverable.file.preview_path, FileHashPreviewSize.MD);
            }

            return (
                <FilePreview
                    useOnlySrcPresenceCheck
                    className="mr-1"
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    style={{ cursor: 'pointer' }}
                    extension={taskDeliverable.file.extension}
                    src={previewUrl}
                    onClick={onOpenViewer}
                />
            );
        }

        // Get icon
        let icon: JSX.Element;
        switch (taskDeliverable.type) {
            case TaskDeliverableType.BLANK:
                icon = <Icon className="mr-1" icon="circle" size={96} />;
                break;
            case TaskDeliverableType.TEXT:
                icon = <Icon className="mr-1" icon="text-highlight" size={96} />;
                break;
            case TaskDeliverableType.EMBED:
                icon = <Icon className="mr-1" icon="insert" size={96} />;
                break;
            default:
                icon = <Icon className="mr-1" icon="error" size={96} />;
        }

        return (
            <Flex
                fullHeight
                fullWidth
                align="center"
                justify="center"
                style={{ cursor: 'pointer' }}
                onClick={onOpenViewer}
            >
                {icon}
            </Flex>
        );
    };

    const renderCommentsCounter = () => {
        const metadata = taskDeliverable.metadata;

        if (metadata.comment_count_total === 0) {
            return <DevText muted>No comments</DevText>;
        }

        if (metadata.comment_count_unresolved === 0) {
            // No unresolved comments
            return (
                <DevText muted>
                    <Icon className="mr-small" icon="chat" />
                    {metadata.comment_count_total} total
                </DevText>
            );
        } else {
            return (
                <Tooltip content={`${metadata.comment_count_total} total`}>
                    <DevText>
                        <Icon className="mr-small" icon="chat" intent={Intent.PRIMARY} />
                        {metadata.comment_count_unresolved} unresolved
                    </DevText>
                </Tooltip>
            );
        }
    };

    return (
        <Card compact elevation={Elevation.ONE} {...props}>
            <Box className="mb-1" borderRadius="inherit" height="280px" minHeight="280px" width="100%">
                {renderTaskDeliverablePreview()}
            </Box>

            <div>
                <Flex className="mb-small" direction="row" align="center" justify="space-between">
                    <Tooltip content={taskDeliverable.title}>
                        <Link className="mr-1" onClick={onOpenViewer}>
                            {TextFormatUtils.truncate(taskDeliverable.title, 45)}
                        </Link>
                    </Tooltip>

                    {actions && <Flex align="center">{actions}</Flex>}
                </Flex>

                <Flex direction="row" align="center" justify="space-between">
                    <Flex direction="row" align="center">
                        {renderTaskDeliverableApprovementTag()}
                        {renderCommentsCounter()}
                    </Flex>

                    <DevText muted className="ml-auto">
                        {moment(taskDeliverable.created_at).format('D MMM, YYYY')}
                    </DevText>
                </Flex>
            </div>
        </Card>
    );
};

export default TaskDeliverableListGridViewListItem;
